import { ColumnDef } from "@tanstack/react-table";
import { Task } from "@/types/task";
import { TaskStatus } from "@/consts/Task";
import TaskSelect from "@/pages/Project/Detail/columns/components/TaskSelect";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import DataTableColumnHeader from "@/components/Common/DataTable/DataTableColumnHeader";
import { calculateDDay, cn, formatDate } from "@/libs/utils";
import { Plus } from "lucide-react";

const statusOrder = [
  TaskStatus.OPENED,
  TaskStatus.IN_PROGRESS,
  TaskStatus.REVIEW,
  TaskStatus.CLOSED,
  TaskStatus.CANCELED,
] as const;

const statusSortingFn = (rowA: any, rowB: any, columnId: any) => {
  const statusA = rowA.getValue(columnId);
  const statusB = rowB.getValue(columnId);

  return (
    statusOrder.indexOf(statusA as TaskStatus) -
    statusOrder.indexOf(statusB as TaskStatus)
  );
};

export const createTaskColumns = (
  onRowClick: (projectGroupId: string) => void
): ColumnDef<Task & { disabled?: boolean }>[] => [
  {
    accessorKey: "project_group_title",
    header: ({ column }) => (
      <DataTableColumnHeader title="프로젝트명" column={column} />
    ),
    cell: ({ row }) => {
      const { project_group_title, project_group_id } = row.original;

      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRowClick(project_group_id);
          }}
          className="text-gray-A text-sm font-normal text-left"
        >
          {project_group_title}
        </button>
      );
    },
    enableSorting: false,
    size: 300,
  },
  {
    accessorKey: "task_title",
    header: ({ column }) => (
      <DataTableColumnHeader title="작업명" column={column} />
    ),
    cell: ({ row }) => {
      const taskTitle: string = row.getValue("task_title");

      return (
        <span className="text-gray-A text-sm font-normal">{taskTitle}</span>
      );
    },
    enableSorting: false,
    size: 300,
  },
  {
    accessorKey: "assignor",
    header: ({ column }) => (
      <DataTableColumnHeader title="요청자" column={column} />
    ),
    cell: ({ row }) => {
      const { assignor } = row.original;
      return (
        <div className="flex items-center gap-2">
          <Avatar className="size-6">
            <AvatarImage src={assignor.img_url} />
            <AvatarFallback>R</AvatarFallback>
          </Avatar>
          <span className="whitespace-nowrap text-xs text-gray-A font-normal">
            {assignor.user_name}
          </span>
        </div>
      );
    },
    size: 100,
    enableSorting: false,
  },
  {
    accessorKey: "assignees",
    header: ({ column }) => (
      <DataTableColumnHeader title="작업자" column={column} />
    ),
    cell: ({ row }) => {
      const { assignees, disabled } = row.original;
      return (
        <>
          {assignees.length === 1 ? (
            <div className="flex items-center gap-2">
              <Avatar className="size-6">
                <AvatarImage src={assignees[0].img_url} />
                <AvatarFallback>R</AvatarFallback>
              </Avatar>
              <span className="whitespace-nowrap text-xs text-gray-A font-normal">
                {assignees[0].user_name}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              {assignees.slice(0, 2).map((assignees, index) => (
                <div
                  className="flex items-center gap-2"
                  key={`assignees-${assignees.user_id}-${index}`}
                >
                  <Avatar className="size-6">
                    <AvatarImage src={assignees.img_url} />
                    <AvatarFallback>{assignees.user_name}</AvatarFallback>
                  </Avatar>
                  <span className="text-xs text-gray-A font-normal">
                    {assignees.user_name}
                  </span>
                </div>
              ))}
              {assignees.length > 2 && (
                <div className="flex items-center text-gray-B">
                  <Plus className="size-3.5" />
                  <span className="text-sm font-semibold">
                    {assignees.length - 2}
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      );
    },
    size: 215,
    enableSorting: false,
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader title="진행상태" column={column} />
    ),
    cell: ({ row }) => {
      const status: TaskStatus = row.getValue("status");
      const { task_id, project_group_id, disabled } = row.original;

      return (
        <TaskSelect
          status={status}
          taskId={task_id}
          groupId={project_group_id}
          disabled={disabled}
        />
      );
    },
    sortingFn: statusSortingFn,
    size: 100,
    meta: {
      isInteractive: true,
    },
  },
  {
    accessorKey: "latest_user_memo",
    header: ({ column }) => (
      <DataTableColumnHeader title="작업 메모" column={column} />
    ),
    cell: ({ row }) => {
      const latestUserMemo: string = row.getValue("latest_user_memo");

      return (
        <span className="text-gray-A text-sm font-normal line-clamp-2">
          {latestUserMemo}
        </span>
      );
    },
    size: 270,
    enableSorting: false,
  },
  {
    accessorKey: "due_date",
    header: ({ column }) => (
      <DataTableColumnHeader title="작업 기간" column={column} />
    ),
    cell: ({ row }) => {
      const dueDate = row.getValue<number>("due_date");
      const createdAt = row.original.created_at;
      const dDay = calculateDDay(dueDate);

      return (
        <div className="flex flex-col gap-2 text-xs text-gray-A font-normal">
          <p>{formatDate(createdAt)} ~</p>
          <p
            className={cn({
              "text-red font-bold": dDay <= 3 && dDay >= 0, // D-3일 이하일 때 빨간색 강조
            })}
          >
            {formatDate(dueDate)}
          </p>
        </div>
      );
    },
    size: 150,
  },
];
